import { Dispatch, SetStateAction, useState } from 'react';

import { useMutation } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { uploadFileToGcloud } from '@api/common';
import { getTaskUploadURLAttachment } from '@api/portalTasks';

import { handleErrorFromMessage } from '@utils/handleRequestError';
import { generateHeaders } from '@utils/headersMapper';

import { IAssetFile } from '@interfaces/assets/asset';
import { IResponseError } from '@interfaces/errors/apiErrors';

import { ESnackbarTypes, TMIMETypes } from '@enums/common';

import { HUNDRED_PERCENT_LOADING } from '@constants/common';
import { UPLOAD_URL_ATTACHMENT_PORTAL_TASK } from '@constants/queryKeys';

interface IUseAttachFilesParams {
	setUploadedFiles: Dispatch<SetStateAction<Array<IAssetFile>>>;
}

export const useMutatePortalTaskAttachFiles = (props: IUseAttachFilesParams) => {
	const [progress, setProgress] = useState<number>(HUNDRED_PERCENT_LOADING);
	const { setUploadedFiles } = props;
	const dispatch = useAppDispatch();

	const { mutate: createTaskUploadUrlAttachment } = useMutation({
		mutationKey: [UPLOAD_URL_ATTACHMENT_PORTAL_TASK],
		mutationFn: async ({ files }: { files: Array<File> }) => {
			const uploadPromises = files.map(async (file) => {
				try {
					const data = await getTaskUploadURLAttachment({ fileName: file.name });
					const headers = generateHeaders(data?.headers);
					const url = data?.uploadUrl;

					await uploadFileToGcloud({
						url,
						data: file,
						headers,
						onProgressHandler: setProgress,
					});

					return {
						fileData: {
							name: file.name,
							id: data.fileId || data.importId,
							size: file.size,
							mimeType: file.type as TMIMETypes,
						},
					};
				} catch (error) {
					return {
						fileName: file.name,
						error,
					};
				}
			});

			const results = await Promise.allSettled(uploadPromises);

			const successfullyUploadedFiles = results
				.filter(result => result.status === 'fulfilled')
				.map(result => (result as any).value.fileData);

			const failedFileNames = results
				.filter(result => result.status === 'rejected')
				.map(result => (result as any).reason.fileName);

			if (successfullyUploadedFiles.length > 0) {
				setUploadedFiles(prevState => [...prevState, ...successfullyUploadedFiles]);
			}

			if (failedFileNames.length > 0) {
				dispatch(setSnackbar({
					message: `Failed to upload files: ${failedFileNames.join(', ')}`,
					type: ESnackbarTypes.error,
				}));
			}
		},
		onError: (err: IResponseError) => {
			handleErrorFromMessage(err);
		},
	});

	return {
		createTaskUploadUrlAttachment,
		progress,
	};
};
